import { useFormContext } from 'react-hook-form';
import type { User } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useUsers } from '../api';
import { useGridSelection, useUsersColumns } from '../hooks';
import { BaseGrid } from 'components/common';
import { SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash-es';
import { useListParams } from 'hooks';
import { usePermissionGuard } from '../../../login';

const UsersGridComponent = memo(() => {
  const { accessToChangeUserprofile } = usePermissionGuard();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setQ, setApps, q: urlQ, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    q: string | undefined;
    apps: string[] | undefined;
  }>({
    q: urlQ,
    apps: urlApps as string[],
  });

  const filterByEmail = watch('q') as string;
  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (q: string, apps?: string[]) => {
      setDebouncedFilters({ q, apps });
      setQ(q ?? undefined);
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setQ, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByEmail, filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByEmail, filterByApps, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedUsers');
  const navigate = useNavigate();

  const columns = useUsersColumns();

  const {
    data: { data: users, count },
    isLoading,
  } = useUsers({
    params: {
      ...(debouncedFilters.q && { email: debouncedFilters.q }),
      ...(debouncedFilters.apps && { apps: debouncedFilters.apps }),
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeUserprofile && navigate(`/cabinet/facebook/users/${id}`);
    },
    [navigate, accessToChangeUserprofile],
  );

  return (
    <BaseGrid<User>
      rows={users}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.username}
      onRowClick={(params) => {
        handleClick((params.row as User).id);
      }}
    />
  );
});

UsersGridComponent.displayName = 'UsersGridComponent';

const UsersGrid = () => {
  return (
    <SuspenseBoundary>
      <UsersGridComponent />
    </SuspenseBoundary>
  );
};

export default UsersGrid;
